<template>
    <div class="library-col">
        <Card class="panel library-panel">
            <template #title>
                <div class="panel-head">
                    <span class="panel-icon"><i class="pi pi-folder-open" /></span>
                    <span class="H6-Medium panel-head-title">Paper library</span>
                    <span class="Caption panel-head-caption">
                        {{ pagination.totalRecords }}
                        paper{{ pagination.totalRecords === 1 ? '' : 's' }} in the library
                    </span>
                </div>
            </template>

            <template #content>
                <div class="panel-body">
                    <section class="filter-bar">
                        <div class="filter-bar-head">
                            <span class="filter-bar-title">Filters</span>
                            <span v-if="activeFilterCount" class="filter-count">
                                {{ activeFilterCount }} active
                            </span>
                        </div>

                        <div class="filter-grid">
                            <IconField class="filter-search">
                                <InputIcon class="pi pi-search" />
                                <InputText v-model="filters.searchTerm" placeholder="Search paper name"
                                    class="w-full" size="small" variant="filled"
                                    @keyup.enter="fetchPapers(1)" />
                            </IconField>
                            <Select v-model="filters.grade" :options="gradeOptions"
                                :loading="isLoadingGrades" optionLabel="name" placeholder="Grade"
                                size="small" variant="filled" show-clear @change="onFilterGradeChange" />
                            <Select v-model="filters.subject" :options="availableFilterSubjects"
                                optionLabel="name"
                                :disabled="!filters.grade || availableFilterSubjects.length === 0"
                                placeholder="Subject" size="small" variant="filled" show-clear filter />
                            <Select v-model="filters.year" :options="yearFilterChoices" placeholder="Year"
                                size="small" variant="filled" show-clear filter />
                            <Select v-model="filters.medium" :options="mediumChoices" optionLabel="label"
                                optionValue="value" placeholder="Medium" size="small" variant="filled"
                                show-clear />
                            <Select v-model="filters.term" :options="termList" optionLabel="name"
                                optionValue="code" placeholder="Term" size="small" variant="filled"
                                show-clear />
                        </div>

                        <div class="filter-actions">
                            <Button label="Clear" icon="pi pi-filter-slash" size="small"
                                class="ghost-button" :disabled="!hasActiveFilters" @click="clearFilters" />
                            <Button label="Apply" icon="pi pi-filter" size="small" class="accent-button"
                                @click="fetchPapers(1)" />
                        </div>
                    </section>

                    <div v-if="papers.length" class="papers-list">
                        <article v-for="paper in papers" :key="paper._id" class="paper-row">
                            <span class="paper-row-icon"><i class="pi pi-file-pdf" /></span>

                            <div class="paper-row-title">
                                <span class="paper-name" :title="paper.name">{{ paper.name }}</span>
                                <Tag v-if="paper.status === 'archived'" severity="danger" value="Archived"
                                    class="mini-tag" />
                            </div>

                            <!-- Year, term and medium are optional: a paper without one simply shows
                                 one chip fewer. -->
                            <div class="paper-meta">
                                <span class="meta-chip">{{ cleanupLables(paper.gradeName) }}</span>
                                <span class="meta-chip">{{ formatSubjectName(paper.subject) }}</span>
                                <span v-if="paper.year" class="meta-chip">{{ paper.year }}</span>
                                <span v-if="paper.term" class="meta-chip">
                                    {{ cleanupLables(paper.term) }}
                                </span>
                                <span v-if="paper.medium" class="meta-chip">
                                    {{ cleanupLables(paper.medium) }}
                                </span>
                            </div>

                            <div class="file-pills">
                                <span class="file-pill"
                                    :class="{ 'is-empty': !paper.fileCounts.papers }">
                                    <i class="pi pi-file-pdf" />{{ paper.fileCounts.papers }} paper{{
                                        paper.fileCounts.papers === 1 ? '' : 's' }}
                                </span>
                                <span class="file-pill"
                                    :class="{ 'is-empty': !paper.fileCounts.markingSchemes }">
                                    <i class="pi pi-check-square" />{{ paper.fileCounts.markingSchemes }}
                                    marking scheme{{ paper.fileCounts.markingSchemes === 1 ? '' : 's' }}
                                </span>
                                <span class="file-pill"
                                    :class="{ 'is-empty': !paper.fileCounts.answerSheets }">
                                    <i class="pi pi-pencil" />{{ paper.fileCounts.answerSheets }}
                                    answer sheet{{ paper.fileCounts.answerSheets === 1 ? '' : 's' }}
                                </span>
                            </div>

                            <div class="paper-row-actions">
                                <Button icon="pi pi-pencil" class="row-action"
                                    @click="emit('edit', paper)" v-tooltip.bottom="'Edit details'" />
                                <Button icon="pi pi-paperclip" class="row-action"
                                    @click="emit('manage', paper)" v-tooltip.bottom="'Manage files'" />
                                <Button icon="pi pi-trash" class="row-action danger"
                                    @click="removePaper(paper)" v-tooltip.bottom="'Delete paper'" />
                            </div>
                        </article>
                    </div>

                    <div v-else class="empty-state">
                        <span class="empty-icon"><i class="pi pi-inbox" /></span>
                        <span class="empty-title">No past papers found</span>
                        <span class="empty-hint">
                            {{ hasActiveFilters
                                ? 'Nothing matches these filters — try clearing a few.'
                                : 'Add your first paper from the upload panel.' }}
                        </span>
                    </div>

                    <Paginator v-if="pagination.totalPages > 1" :rows="pagination.limit"
                        :totalRecords="pagination.totalRecords"
                        :first="(pagination.currentPage - 1) * pagination.limit" @page="onPageChange" />
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, defineEmits, defineExpose } from 'vue'
import { Button, Card, Select } from 'primevue'
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Tag from 'primevue/tag';
import Paginator from 'primevue/paginator';
import { useConfirm } from 'primevue/useconfirm';
import { notifyError, notifySuccess } from '@/utils/notification';
import { listPapersForAdmin, deletePaper } from '@/api/papers';
import { cleanupLables, formatSubjectName } from '@/utils/cleanups';
import { PAPER_MEDIUM_OPTIONS } from '@/utils/paperFiles';
import { termList, gradeOptions, isLoadingGrades } from '@/utils/options';

const emit = defineEmits(['edit', 'manage']);

const confirm = useConfirm();

const mediumChoices = PAPER_MEDIUM_OPTIONS;

const papers = ref([]);
const pagination = ref({ totalRecords: 0, currentPage: 1, totalPages: 0, limit: 20 });

const filters = reactive({ grade: null, subject: null, year: null, medium: null, term: null, searchTerm: '' });

const availableFilterSubjects = computed(() => filters.grade?.subjects || []);

const yearFilterChoices = computed(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 40 }, (_, i) => current - i);
});

const activeFilterCount = computed(() =>
    [filters.grade, filters.subject, filters.year, filters.medium, filters.term, filters.searchTerm]
        .filter(Boolean).length
);

const hasActiveFilters = computed(() => activeFilterCount.value > 0);

const onFilterGradeChange = () => {
    filters.subject = null;
};

const clearFilters = () => {
    filters.grade = null;
    filters.subject = null;
    filters.year = null;
    filters.medium = null;
    filters.term = null;
    filters.searchTerm = '';
    fetchPapers(1);
};

const fetchPapers = async (page = 1) => {
    try {
        const res = await listPapersForAdmin({
            grade: filters.grade?.code,
            subject: filters.subject?.code,
            year: filters.year,
            medium: filters.medium,
            term: filters.term,
            searchTerm: filters.searchTerm,
            status: 'all',
            page,
            limit: pagination.value.limit,
        });
        const data = res.data?.data || {};
        papers.value = data.papers || [];
        pagination.value = data.pagination || pagination.value;
    } catch (err) {
        papers.value = [];
        notifyError("Error", err?.response?.data?.message || "Failed to load past papers.");
    }
};

const onPageChange = (event) => fetchPapers(event.page + 1);

// Deleting takes the paper's files with it, so it asks for confirmation first.
const removePaper = (paper) => {
    confirm.require({
        message: `Do you want to delete "${paper.name}" and its files?`,
        header: 'Danger Zone',
        icon: 'pi pi-info-circle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Delete',
            severity: 'danger'
        },
        accept: async () => {
            try {
                await deletePaper(paper._id);
                notifySuccess("Deleted!", `"${paper.name}" and its files have been removed.`);
                fetchPapers(pagination.value.currentPage);
            } catch (err) {
                notifyError("Error", err?.response?.data?.message || "Failed to delete the paper.");
            }
        },
    });
};

const applyPaper = (updated) => {
    if (!updated) return;
    const index = papers.value.findIndex(p => p._id === updated._id);
    if (index !== -1) papers.value[index] = updated;
};

const refresh = (page) => fetchPapers(page ?? pagination.value.currentPage);

onMounted(() => fetchPapers());

defineExpose({ refresh, applyPaper });
</script>

<style scoped>
/* ---------- Layout ---------- */

.library-col {
    min-width: 0;
}

@media (min-width: 1201px) {
    .library-col {
        position: relative;
    }

    .library-panel {
        position: absolute;
        inset: 0;
    }
}

/* ---------- Panel ---------- */

.panel {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border: 1px solid var(--black-50);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    overflow: hidden;
}

.panel :deep(.p-card-body) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;
    gap: 0;
}

.panel :deep(.p-card-caption) {
    gap: 0;
}

.panel :deep(.p-card-content) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.panel-head {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-rows: minmax(2.5rem, auto) auto;
    align-items: center;
    column-gap: 0.75rem;
    row-gap: 0.2rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--black-50);
    background: linear-gradient(180deg, #F7F9FD 0%, #FFFFFF 100%);
}

.panel-head-title {
    grid-column: 2;
    grid-row: 1;
}

.panel-head-caption {
    grid-column: 2 / -1;
    grid-row: 2;
}

.panel-icon {
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 10px;
    background-color: var(--accent-100);
    color: var(--accent-700);
}

.panel-icon i {
    font-size: 1.05rem;
}

.panel-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-height: 0;
    padding: 1.25rem;
}

.library-panel .panel-body {
    gap: 1rem;
}

/* ---------- Filters ---------- */

.filter-bar {
    padding-bottom: 1rem;
}

.filter-bar-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.65rem;
}

.filter-bar-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--gray-600);
}

.filter-count {
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background-color: var(--gray-200);
    color: var(--black-700);
    font-size: 0.66rem;
    font-weight: 500;
}

.filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr));
    gap: 0.5rem;
}

.filter-grid :deep(.p-select) {
    width: 100%;
}

.filter-search {
    grid-column: 1 / -1;
}

.filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.65rem;
}

.ghost-button.p-button {
    height: 32px;
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--black-50);
    background-color: #fff;
    color: var(--gray-600);
    font-size: 14px;
    transition: background-color 0.2s, border-color 0.2s;
}

.ghost-button.p-button:not(:disabled):hover {
    background-color: var(--gray-200);
    border-color: var(--black-50);
    color: var(--gray-600);
}

/* ---------- Paper rows ---------- */

.papers-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.25rem;
}

.paper-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    column-gap: 0.75rem;
    row-gap: 0.4rem;
    align-items: center;
    padding: 0.75rem 0.85rem;
    border: 1px solid var(--black-50);
    border-radius: 10px;
    background-color: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.paper-row:hover {
    border-color: var(--accent-100);
    box-shadow: 0 2px 10px rgba(30, 40, 60, 0.07);
}

.paper-row-icon {
    grid-column: 1;
    grid-row: 1 / span 3;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 8px;
    border: 1px solid var(--accent-100);
    background-color: var(--accent-50);
    color: var(--accent-500);
}

.paper-row-icon i {
    font-size: 0.95rem;
}

.paper-row-title {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    min-width: 0;
}

.paper-name {
    max-width: 100%;
    font-size: 0.92rem;
    font-weight: 500;
    color: var(--gray-600);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.paper-meta {
    grid-column: 2 / -1;
    grid-row: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    padding: 0.14rem 0.42rem;
    border-radius: 6px;
    background-color: var(--accent-50);
    font-size: 0.7rem;
    color: var(--black-700);
}

.file-pills {
    grid-column: 2 / -1;
    grid-row: 3;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.file-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    border: 1px solid var(--accent-100);
    background-color: var(--accent-50);
    color: #3D5A99;
    font-size: 0.66rem;
    font-weight: 500;
}

.file-pill i {
    font-size: 0.6rem;
}


.file-pill.is-empty {
    background-color: var(--accent-50);
    border-color: transparent;
    color: var(--black-200);
}

.paper-row-actions {
    grid-column: 3;
    grid-row: 1;
    justify-self: end;
    display: flex;
    gap: 0.15rem;
    flex-shrink: 0;
}

@media (max-width: 520px) {
    .paper-row {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .paper-row-title {
        grid-column: 2 / -1;
    }

    .paper-row-actions {
        grid-column: 1 / -1;
        grid-row: 4;
        justify-self: stretch;
        justify-content: flex-end;
    }
}

.row-action.p-button {
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background-color: transparent;
    color: var(--black-700);
    transition: background-color 0.15s ease, color 0.15s ease;
}

.row-action.p-button:not(:disabled):hover {
    background-color: var(--accent-50);
    color: var(--accent-700);
}

.row-action.danger.p-button:not(:disabled):hover {
    background-color: #FEE2E2;
    color: #B91C1C;
}

.row-action.p-button:hover,
.row-action.p-button:focus,
.row-action.p-button:active,
.ghost-button.p-button:focus,
.ghost-button.p-button:active {
    border-color: transparent;
    outline: none;
    box-shadow: none;
}

.ghost-button.p-button:hover {
    outline: none;
    box-shadow: none;
}

.row-action.p-button:focus-visible,
.ghost-button.p-button:focus-visible {
    outline: 1px solid var(--black-700);
    outline-offset: 1px;
    box-shadow: none;
}

.row-action :deep(.p-button-icon) {
    font-size: 0.8rem;
}

.mini-tag {
    font-size: 0.62rem;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    line-height: 1.5;
}

/* ---------- Empty state ---------- */

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    padding: 2.5rem 1rem;
    border: 1px dashed var(--black-50);
    border-radius: 12px;
    background-color: #FCFDFE;
    text-align: center;
}

.library-panel .empty-state {
    flex: 1;
    justify-content: center;
}

.empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    background-color: var(--accent-50);
    color: var(--accent-500);
}

.empty-icon i {
    font-size: 1.2rem;
}

.empty-title {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--gray-600);
}

.empty-hint {
    max-width: 24rem;
    font-size: 0.75rem;
    line-height: 1.5;
    color: var(--black-700);
}

.library-panel :deep(.p-paginator) {
    padding: 0.75rem 0 0;
    background: transparent;
}
</style>
